import React from "react";
import * as SubframeCore from "@subframe/core";
import { Button } from "subframe/components/Button";
import { formatString } from "modules/Retention/utils/recognition";
import RETENTION_ROUTES from "modules/Retention/constants/routes";
import { useNavigate } from "react-router-dom";
import { SENTIMENT_SCORE_MAPPER } from "modules/Retention/pages/TaskCenter/components/TaskCenterTable";

interface TaskCardProps {
  type: 'at-risk' | 'in-progress';
  count: number;
  title: string;
  items: Array<{
    count: number;
    label: string;
  }>;
  dueText?: string;
}

const TaskCard: React.FC<TaskCardProps> = ({
  type,
  count,
  title,
  items,
  dueText,
}) => {
  const bgColor = type === 'at-risk' ? 'bg-error-100' : 'bg-warning-100';
  const textColor = type === 'at-risk' ? 'text-error-700' : 'text-warning-700';

  return (
    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-lg bg-default-background shadow-sm w-full h-full">
      <div className={`flex w-full flex-col items-start gap-2 rounded-[8px] ${bgColor} pl-6 pr-3 pt-2 pb-4`}>
        <div className="flex items-center gap-2">
          <SubframeCore.Icon
            className={`text-header font-header ${textColor}`}
            name={type === 'at-risk' ? "FeatherAlertCircle" : "FeatherClock"}
          />
          <span className={`text-header font-header ${textColor}`}>
            {count}
          </span>
        </div>
        <span className="text-subheader font-subheader text-default-font">
          {title}
        </span>

      </div>
      <div className="flex w-full flex-1 flex-col items-start gap-2 px-6 pb-6">
        <div className="flex w-full flex-col items-start gap-1">
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex h-2 w-2 flex-none items-start rounded-full bg-subtext-color" />
              <span className="text-body font-body text-subtext-color">
                {item.count} {SENTIMENT_SCORE_MAPPER[item.label as keyof typeof SENTIMENT_SCORE_MAPPER] || formatString(item.label)}

              </span>
            </div>
          ))}
        </div>
        <div className="flex flex-1 flex-col justify-end gap-2">
          {dueText && (
            <div className="flex w-full items-center justify-between pt-2">
              <span className={`text-body font-body ${type === 'at-risk' ? 'text-error-700' : 'text-error-600'}`}>
                {dueText}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface PriorityTasksProps {
  atRiskTasks: {
    count: number;
    items: Array<{ count: number; label: string }>;
    dueText: string;
  };
  inProgressTasks: {
    count: number;
    items: Array<{ count: number; label: string }>;
    dueText: string;
  };
}

export const PriorityTasks: React.FC<PriorityTasksProps> = ({ atRiskTasks, inProgressTasks }) => {
  const navigate = useNavigate();
  
  // Provide default values if props are undefined or null
  const safeAtRiskTasks = atRiskTasks || { count: 0, items: [], dueText: "" };
  const safeInProgressTasks = inProgressTasks || { count: 0, items: [], dueText: "" };
  
  return (
    <div className="flex w-full max-w-[1024px] flex-col items-start gap-4">
      <div className="flex w-full flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <span className="text-section-header font-section-header text-default-font">
          Priority Tasks
        </span>
        <Button
          disabled={false}
          variant="brand-tertiary"
          size="medium"
          onClick={() => {
            navigate(RETENTION_ROUTES.TASK_CENTER);
          }}
        >
          View the full list
        </Button>
      </div>
      <div className="flex w-full grow shrink-0 basis-0 flex-col md:flex-row items-start gap-4">
        <TaskCard
          type="at-risk"
          count={safeAtRiskTasks.count || 0}
          title="Action Needed"
          items={safeAtRiskTasks.items || []}
          dueText={safeAtRiskTasks.dueText || ""}
        />
        <TaskCard
          type="in-progress"
          count={safeInProgressTasks.count || 0}
          title="In Progress"
          items={safeInProgressTasks.items || []}
          dueText={safeInProgressTasks.dueText || ""}
        />
      </div>
    </div>
  );
}; 